import React, { useState } from 'react';
import { X } from 'lucide-react';
import { AssetRecord } from '../types';

interface Props {
  assets: AssetRecord[];
  onClose: () => void;
  onSubmit: (data: {
    asset_id: string;
    inspection_type: string;
    planned_date: string;
    inspector_name: string;
  }) => Promise<void>;
}

export const AssetInspectionModal: React.FC<Props> = ({ assets, onClose, onSubmit }) => {
  const [assetId, setAssetId] = useState(assets[0]?.id || '');
  const [inspectionType, setInspectionType] = useState('RAMP');
  const [plannedDate, setPlannedDate] = useState(new Date().toISOString().slice(0, 10));
  const [inspectorName, setInspectorName] = useState('Quality & Airworthiness Inspector');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await onSubmit({
        asset_id: assetId,
        inspection_type: inspectionType,
        planned_date: plannedDate,
        inspector_name: inspectorName,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while scheduling asset inspection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Schedule Physical Asset Inspection</h3>
            <p>Plan ramp checks, pre-flight assessments, or periodic asset verifications</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div className="form-field">
              <label>Target Asset *</label>
              <select
                value={assetId}
                onChange={(e) => setAssetId(e.target.value)}
              >
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    [{a.asset_domain}] {a.asset_number} — {a.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Inspection Type *</label>
                <select
                  value={inspectionType}
                  onChange={(e) => setInspectionType(e.target.value)}
                >
                  <option value="RAMP">RAMP (Apron / Field Check)</option>
                  <option value="DAILY">DAILY (Daily Inspection)</option>
                  <option value="PRE_FLIGHT">PRE_FLIGHT (Pre-flight Check)</option>
                  <option value="WEEKLY">WEEKLY (Weekly Maintenance)</option>
                  <option value="ANNUAL">ANNUAL (Comprehensive Annual)</option>
                </select>
              </div>

              <div className="form-field">
                <label>Planned Date *</label>
                <input
                  required
                  type="date"
                  value={plannedDate}
                  onChange={(e) => setPlannedDate(e.target.value)}
                />
              </div>
            </div>

            <div className="form-field">
              <label>Lead Inspector Name *</label>
              <input
                required
                type="text"
                value={inspectorName}
                onChange={(e) => setInspectorName(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Scheduling...' : 'Schedule Inspection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
